import { useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Controller, useFormContext } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Tooltip from '@components/Tooltip';
import HelpIcon from '@mui/icons-material/Help';
import { Input } from '@components/Input';
import PhoneInput from '@components/PhoneInput';
import ComboLocationSelect from '@features/ComboLocationSelect';
import { Select } from '@components/Select';
import { BASE_URL } from '@helpers/api';
import { SmsProviderType } from '@constants/channels';
import { useTranslation, useCopyToClipboard, useThrottle, useLocations } from '@hooks';
import { setInitialPhoneNumberToForm } from '@helpers/phone';
import { CopyIcon } from 'assets/images/icons';

const Container = styled('div')({
    display: 'grid',
    gridGap: '16px 24px',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    '@media (max-width: 600px)': {
        gridTemplateColumns: '100%',
    },
    '& .combo-location-select': {
        gridColumn: '1 / span 2',
        '& .location-type': {
            marginTop: '-16px',
        },
    },
});

const providerTypeOptions = [
    { value: 0, label: 'Twilio' },
    { value: 1, label: 'TurboSMS' },
    { value: 2, label: 'Lifecell' },
    { value: 3, label: ' SMS.to' },
];

const SmsForm = ({ accountLocale, isEdit }) => {
    const brands = useSelector(state => state.brands.data);

    const { t } = useTranslation();
    const { getSelectLocationsOptions } = useLocations();
    const { onCopy } = useCopyToClipboard();
    const { control, setValue, watch, twillioUid, selectedChannel } = useFormContext();
    const isMobile = useMediaQuery('@media (max-width: 600px)');

    const selectedProvider = watch('provider');

    const [onCopyClick] = useThrottle(value => onCopy(value), 1000);

    useEffect(() => {
        if (!isEdit) {
            setValue('phoneNumber', setInitialPhoneNumberToForm('', accountLocale), {
                shouldValidate: false,
                shouldTouch: true,
            });
        }
    }, [setValue, accountLocale]);

    const brandsOptions = useMemo(() => brands.map(item => ({ value: item.id, label: item.title })), [brands]);

    const locationsOptions = useMemo(() => getSelectLocationsOptions(), []);

    return (
        <Container>
            <Controller
                control={control}
                name="name"
                render={({ field, fieldState: { error }, ref }) => (
                    <Input
                        ref={ref}
                        inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                        label={t('SettingsChannels.channelName')}
                        fullWidth
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                        required
                    />
                )}
            />
            <Controller
                control={control}
                name="provider"
                render={({ field, fieldState: { error }, ref }) => (
                    <Select
                        {...field}
                        ref={ref}
                        onChange={({ target: { value } }) => {
                            setValue('provider', value, { shouldDirty: true });
                        }}
                        options={providerTypeOptions}
                        label={t('SettingsChannels.provider')}
                        fullWidth
                        required
                        error={!!error}
                    />
                )}
            />
            <ComboLocationSelect
                brandsOptions={brandsOptions}
                locationsOptions={locationsOptions}
                data={selectedChannel}
            />
            {selectedProvider === SmsProviderType.Twilio && (
                <>
                    <Controller
                        control={control}
                        name="accountId"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                label={t('SettingsChannels.accountSid')}
                                type="password"
                                fullWidth
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                                required
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="apiKey"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                label={t('SettingsChannels.authToken')}
                                type="password"
                                fullWidth
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                                required
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="phoneNumber"
                        render={({ field, fieldState: { error } }) => (
                            <PhoneInput
                                placeholder=""
                                label={t('common.phoneNumberOpt')}
                                inputProps={{ name: 'phoneNumber' }}
                                value={field.value}
                                autoFormat
                                onChange={value => {
                                    setValue('phoneNumber', value, { shouldValidate: true, shouldDirty: true });
                                }}
                                error={error ? t(error.message) : null}
                            />
                        )}
                    />
                </>
            )}
            {[SmsProviderType.TurboSms, SmsProviderType.SmsTo].includes(selectedProvider) && (
                <Controller
                    control={control}
                    name="token"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('SettingsChannels.apiToken')}
                            type="password"
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            required
                        />
                    )}
                />
            )}
            {selectedProvider === SmsProviderType.Lifecell && (
                <>
                    <Controller
                        control={control}
                        name="login"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                label={t('common.login')}
                                fullWidth
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                                required
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="password"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                label={t('common.password')}
                                type="password"
                                fullWidth
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                                required
                            />
                        )}
                    />
                </>
            )}
            <Controller
                control={control}
                name="senderId"
                render={({ field, fieldState: { error }, ref }) => (
                    <Input
                        ref={ref}
                        inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                        label={t('SettingsChannels.senderId')}
                        fullWidth
                        error={!!error}
                        helperText={
                            error ? (
                                <div style={{ position: 'absolute', top: '-14px', width: '250px' }}>
                                    {t(error.message, { count: 11 })}
                                </div>
                            ) : null
                        }
                        required={selectedProvider !== SmsProviderType.Twilio}
                        addonComponent={
                            <Tooltip title={t('SettingsChannels.senderIdNotice')} placement="right">
                                <HelpIcon sx={{ fontSize: '16px' }} />
                            </Tooltip>
                        }
                    />
                )}
            />
            {selectedProvider === SmsProviderType.Twilio && (
                <Input
                    inputProps={{ value: `${BASE_URL}api/inbox/webhooks/twilio-message/${twillioUid}` }}
                    onAddonClick={() => onCopyClick(`${BASE_URL}api/inbox/webhooks/twilio-message/${twillioUid}`)}
                    addonComponent={<CopyIcon style={{ fill: 'rgba(0, 0, 0, 0.6)' }} />}
                    label="Webhook URL"
                    disabled
                    sx={{ gridColumn: isMobile ? '1 / span 1' : '1 / span 2' }}
                />
            )}
        </Container>
    );
};

export default SmsForm;
